import { SetupDataFetcher, SetupDataFetcherArgs } from './types';

const intervals = {};

export default function setupDataFetcher({
  onResult,
  onPaginatedResult,
  onError,
  generateFetchKey,
  refreshInterval = null,
  maxDataAge = null,
  propParams
}: SetupDataFetcherArgs): SetupDataFetcher {
  const params: Record<string, any> = propParams || {}; // cant set default propParams in args, otherwise causes rerenders
  const baseFetchKey = generateFetchKey();
  const fetchKeyWithParams = generateFetchKey(params);
  let pagesFetched: Array<string> = [];
  const interval = intervals[fetchKeyWithParams];

  // clear out old interval is running this again
  if (interval) {
    clearInterval(interval);
  }

  // setup data refreshing interval
  const setupRefreshInterval = () => {
    if (refreshInterval) {
      intervals[fetchKeyWithParams] = setInterval(() => {
        let dataCollected: Array<any> = [];

        // Big complicated function for dealing with refreshing paginated data
        const collectPaginatedRefreshResults = data => {
          const { body, nextPageUrl } = data;
          // update list of collected data with the new stuff
          dataCollected = [...dataCollected, ...body];

          // Unsubscribe from page we just fetched
          const thisPageIndex =
            nextPageUrl === null
              ? pagesFetched.length - 1 // nextPageUrl of null means this is the last page
              : pagesFetched.findIndex(pageUrl => pageUrl === nextPageUrl) - 1; // else go one page back from the next page
          // Initial page of data will have index of -1 here, since it comes before the first entry in PagesFetched
          if (thisPageIndex === -1) {
            window.UNSUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, collectPaginatedRefreshResults);
          } else {
            const thisPageUrl = pagesFetched[thisPageIndex];
            const fetchKeyForThisPage = generateFetchKey({ ...params, nextPageUrl: thisPageUrl });
            window.UNSUBSCRIBE(`fetch_result_${fetchKeyForThisPage}`, collectPaginatedRefreshResults);
            window.UNSUBSCRIBE(`fetch_error_${fetchKeyForThisPage}`, onError);
          }

          // If the next page is not in the list of pages already fetched, return the entire data set so far
          if (!pagesFetched.includes(nextPageUrl)) {
            onResult({ body: dataCollected, nextPageUrl });
          }
          // If the next page still needs to be refreshed, setup listeners for it and publish a request
          else {
            const fetchKeyForNextPage = generateFetchKey({ ...params, nextPageUrl });
            window.SUBSCRIBE(`fetch_result_${fetchKeyForNextPage}`, collectPaginatedRefreshResults);
            window.SUBSCRIBE(`fetch_error_${fetchKeyForNextPage}`, onError);
            window.PUBLISH(`fetch_get_${baseFetchKey}`, {
              params: { ...params, nextPageUrl },
              timeInterval: maxDataAge ?? refreshInterval
            });
          }
        };

        // If paginated, unsubscribe the base listener, setup listener for returning paginated results in one go
        // else just use the default logic
        if (pagesFetched.length > 0) {
          window.SUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, collectPaginatedRefreshResults);
        }
        // Start the refresh with the first page
        window.PUBLISH(`fetch_get_${baseFetchKey}`, { params, timeInterval: maxDataAge ?? refreshInterval });
      }, refreshInterval);
    }
  };

  const handleFirstResult = message => {
    // If paginated, we don't want our data set to be overwritten in the future by other fetchers requesting the first page
    // So we unsubscribe from the general listener after the first result and rely on the refresh mechanism to get new data
    window.UNSUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, handleFirstResult);
    if (message.nextPageUrl !== null) {
      window.UNSUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, onResult);
    }
  };

  // setup subscriptions
  window.SUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, handleFirstResult);
  window.SUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, onResult);
  window.SUBSCRIBE(`fetch_error_${fetchKeyWithParams}`, onError);
  // request data
  window.PUBLISH(`fetch_get_${baseFetchKey}`, { params, timeInterval: maxDataAge ?? refreshInterval });

  setupRefreshInterval();

  // eslint-disable-next-line default-param-last
  const fetchPageByUrl = (data: any[] = [], nextPageUrl: string) => {
    if (nextPageUrl === undefined) return;
    clearInterval(intervals[fetchKeyWithParams]);
    const fetchKeyForPage = generateFetchKey({ ...params, nextPageUrl });

    const onReceivePaginatedResult = message => {
      window.UNSUBSCRIBE(`fetch_result_${fetchKeyForPage}`, onReceivePaginatedResult);
      window.UNSUBSCRIBE(`fetch_error_${fetchKeyForPage}`, onReceivePaginatedError);
      // record that we have fetched this page of data, and will need to refresh it in the future
      if (!pagesFetched.includes(nextPageUrl)) pagesFetched.push(nextPageUrl);
      onPaginatedResult?.(message, data);
      setupRefreshInterval();
    };

    const onReceivePaginatedError = error => {
      window.UNSUBSCRIBE(`fetch_result_${fetchKeyForPage}`, onReceivePaginatedResult);
      window.UNSUBSCRIBE(`fetch_error_${fetchKeyForPage}`, onReceivePaginatedError);
      onError(error);
    };

    window.SUBSCRIBE(`fetch_result_${fetchKeyForPage}`, onReceivePaginatedResult);
    window.SUBSCRIBE(`fetch_error_${fetchKeyForPage}`, onReceivePaginatedError);
    window.PUBLISH(`fetch_get_${baseFetchKey}`, {
      params: { ...params, nextPageUrl },
      timeInterval: maxDataAge ?? refreshInterval
    });
  };

  return {
    disposer: () => {
      window.UNSUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, handleFirstResult);
      window.UNSUBSCRIBE(`fetch_result_${fetchKeyWithParams}`, onResult);
      window.UNSUBSCRIBE(`fetch_error_${fetchKeyWithParams}`, onError);
      clearInterval(intervals[fetchKeyWithParams]);
      pagesFetched = [];
    },
    fetchPageByUrl
  };
}
